import React, { ReactNode } from 'react'
import { format, money, number } from '../../util/formatters'
import { ClassValue } from 'classnames/types'
import classNames from 'classnames'
import { Icon } from '@blueprintjs/core'
import { Popover } from '../ui/popover'
import { Marketplace, Marketplaces } from '../../util/marketplaces'
import { BillingPlan } from './plans'
import { Button } from '../ui/button'

interface PlanCardProps {
    plan: BillingPlan
    interval: Interval
    onSubscribe: (plan: BillingPlan) => void
}

export enum Interval {
    Monthly = 'monthly',
    Yearly = 'yearly',
}

const AdAttributedSalesTooltip = ({ plan }: { plan: BillingPlan }) => {
    return (
        <div className="p-3 max-w-md text-sm">
            Advertising attributed sales is the amount of sales generated from your ads. It does not
            include organic sales or Sponsored Display attributed revenue.
            <br />
            <br />
            Example A:
            <Table
                v={[
                    ['Ad Attributed Sales', m(plan.included_sales * 0.9)],
                    ['Included Sales', m(-plan.included_sales)],
                    ['Chargeable Sales', '$0.00'],
                    ['Overage Charge', '$0.00'],
                ]}
            />
            <br />
            Example B:
            <Table
                v={[
                    ['Ad Attributed Sales', m(plan.included_sales * 1.1)],
                    ['Included Sales', m(-plan.included_sales)],
                    ['Chargeable Sales', m(plan.included_sales * 1.1 - plan.included_sales)],
                    [
                        'Overage Charge',
                        m((plan.included_sales * 1.1 - plan.included_sales) * plan.rate),
                    ],
                ]}
            />
        </div>
    )
}

// A little helper to format the money as USD and ensure it is a string
const m = (v: number): string => {
    return money(v, Marketplaces[Marketplace.UnitedStates]) ?? ''
}

// Makes a properly right aligned table
const Table = ({ v }: { v: string[][] }) => {
    const maximum_row_length = v
        .map((row) => row.reduce((p, x) => p + x.length, 0) + 1)
        .reduce((a, b) => Math.max(a, b))

    const r = v.map((row) => {
        const row_length = row[0].length + row[1].length + 1
        if (row_length < maximum_row_length) {
            // Add padding
            row[0] += ' '.repeat(maximum_row_length - row_length + 1) + row[1]
        } else {
            row[0] += ' ' + row[1]
        }

        return row[0] + '\n'
    })

    return <pre className="text-xs bg-gray-100 p-1">{r.map((row) => row)}</pre>
}

const AdsTooltip = ({ plan }: { plan: BillingPlan }) => {
    const v = plan.included_ads + 0.9 * plan.ad_pack_amount
    const diff = v - plan.included_ads
    const count = Math.ceil(diff / plan.ad_pack_amount)

    return (
        <div className="p-3 max-w-md text-sm">
            Merch Jar has extra charges for advertising accounts with a large number of ads. Ads are
            defined as the number of effectively enabled ads in your advertising account.
            <br />
            <br />
            Effectively enabled means that the ad itself, it's parent ad group, and it's parent
            campaign are all enabled.
            <br />
            <br />
            Example A:
            <Table
                v={[
                    ['Total Ads', number(plan.included_ads * 0.9) ?? ''],
                    ['Included Ads', number(-plan.included_ads) ?? ''],
                    ['Chargeable Ads', number(0)],
                    ['Overage Charge', m(0)],
                ]}
            />
            <br />
            Example B:
            <Table
                v={[
                    ['Total Ads', number(v)],
                    ['Included Ads', number(-plan.included_ads) ?? ''],
                    ['Chargeable Ads', number(diff)],
                    ['Rate', m(plan.ad_pack_price) + ' / ' + plan.ad_pack_amount / 1000 + 'k ads'],
                    [
                        'Overage Charge',
                        number(count) +
                            ' * ' +
                            m(plan.ad_pack_price) +
                            ' = ' +
                            m(count * plan.ad_pack_price),
                    ],
                ]}
            />
        </div>
    )
}

export const PlanCard = ({ plan, interval, onSubscribe }: PlanCardProps) => {
    return (
        <div
            style={{ maxWidth: '400px' }}
            className="p-4 rounded-md border flex flex-col flex-grow-1 bg-white shadow-md"
        >
            <div className="text-xl font-medium text-center">{plan.name}</div>
            <div className="text-center mt-4 mb-8">{plan.description}</div>
            <Price plan={plan} interval={interval} />

            <div className="px-4">
                <Button
                    fill
                    large
                    text={'Subscribe to ' + plan.name}
                    className="my-6"
                    intent="success"
                    onClick={() => onSubscribe(plan)}
                />
            </div>

            <Feature>
                <div className="flex flex-col">
                    <span className="flex">
                        Up to {planMoney(plan.included_sales)} per mo in&nbsp;
                        <Popover
                            interactionKind="hover"
                            content={<AdAttributedSalesTooltip plan={plan} />}
                            className="border-b border-gray-700 border-dashed cursor-pointer"
                            targetTagName="span"
                        >
                            ad attributed sales
                        </Popover>
                    </span>
                    <span className="text-xs mt-1">
                        Overages billed at {plan.rate * 100}%
                        {interval == Interval.Yearly ? ' per month' : ''}
                    </span>
                </div>
            </Feature>
            <Feature>
                <div className="flex flex-col">{plan.historical_data}</div>
            </Feature>
            <Feature>
                <div className="flex flex-col">{plan.syncing}</div>
            </Feature>
            <Feature>
                <div className="flex flex-col">{plan.automations}</div>
            </Feature>
            <Feature>
                <div className="flex flex-col">
                    <div className="flex">
                        {number(plan.included_ads)} included&nbsp;
                        <Popover
                            interactionKind="hover"
                            content={<AdsTooltip plan={plan} />}
                            className="border-b border-gray-700 cursor-pointer border-dashed"
                        >
                            ads
                        </Popover>
                    </div>
                    <span className="text-xs mt-1">
                        Overages billed at {planMoney(plan.ad_pack_price)}{' '}
                        {interval == Interval.Yearly ? 'per month ' : ''}per{' '}
                        {plan.ad_pack_amount / 1000}k ads
                    </span>
                </div>
            </Feature>
        </div>
    )
}

interface FeatureProps {
    children: ReactNode
}

export const Feature = ({ children }: FeatureProps) => {
    return (
        <div className="flex mb-2">
            <Icon icon="tick" className="text-green-500 mr-2" />
            {children}
        </div>
    )
}

export function planMoney(v: number): string {
    return format(v, 0, false, '$') ?? ''
}

const Price = ({ plan, interval }: { plan: BillingPlan; interval: Interval }) => {
    if (interval == 'monthly') {
        return <PriceText amount={plan.monthly} />
    } else {
        return (
            <div className="flex flex-col items-center">
                <PriceText amount={plan.yearly / 12} />
                <div className="text-gray-400 text-xs">
                    Billed yearly at {planMoney(plan.yearly)} / year
                </div>
            </div>
        )
    }
}

interface PriceTextProps {
    amount: number
    className?: ClassValue
}

export const PriceText = ({ amount, className }: PriceTextProps) => {
    return (
        <div className={classNames('flex flex-col items-center', className)}>
            <div className="text-center flex">
                <span className="self-start" style={{ bottom: '10px' }}>
                    $
                </span>
                <span className="text-2xl font-semibold mb-0">{number(amount, 0)}</span>
            </div>
            <span className="text-xs">per month</span>
        </div>
    )
}
